import { css, keyframes } from 'styled-components'

import { between, down } from '@Globals/Style/breakpoints'

import theme from './theme'

export const buttonReset = css`
  border: none;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: normal;
  -webkit-font-smoothing: inherit;
  -moz-osx-font-smoothing: inherit;
  -webkit-appearance: none;
`

export const linkStylingInner = css`
  color: ${theme.colors.primary};
  text-decoration: underline;

  &:hover {
    color: ${theme.colors.primaryHover};
  }
`

export const linkStyling = css`
  a {
    ${linkStylingInner};
  }
`

interface BoxInputs {
  height?: string
  width: string
}

export const box = ({ height, width }: BoxInputs) => `
  width: ${width};
  height: ${height ?? width};
`

export const circle = (size: string) => css`
  ${box({ width: size })}
  border-radius: 50%;
`

export const truncate = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const flexCenterChildren = css`
  display: flex;
  justify-content: center;
  align-items: center;
`

export const flexCenterChildrenColumn = css`
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const resetButton = css`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`

export const backdrop = css`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${theme.zIndexes.backdrop};
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.25);
`

const loading = keyframes`
  to {
    transform: rotateZ(360deg);
  }
`

interface ImageLoadingCircleInputs {
  borderWidth?: string
  color?: string
  size?: string
}

export const imageLoadingCircle = ({
  borderWidth = '3px',
  color = '#ccc',
  size = '50px',
}: ImageLoadingCircleInputs) => css`
  content: '';
  display: block;
  ${circle(size)}
  border: ${borderWidth} solid transparent;
  border-left-color: ${color};
  border-right-color: ${color};
  animation: ${loading} infinite .75s linear;
`

export const loadingWrap = css`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  ${box({ width: '100%' })};
  background-color: rgba(255, 255, 255, 0.85);
  ${flexCenterChildren};
  display: none;

  &.active {
    display: flex;
  }

  &:after {
    ${imageLoadingCircle({ borderWidth: '3px', size: '60px' })};
    margin-top: -10px;
  }
`

export const sectionOuter = css`
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: center;
  padding-left: ${theme.padding.desktop};
  padding-right: ${theme.padding.desktop};

  ${between('tablet', 'desktop')} {
    padding-left: ${theme.padding.tablet};
    padding-right: ${theme.padding.tablet};
  }

  ${between('mobileSmall', 'tablet')}{
    padding-left: ${theme.padding.phablet};
    padding-right: ${theme.padding.phablet};
  }
`

export const sectionInner = css`
  width: 100%;
  max-width: ${theme.breakpoints.maxWidth};
`

export const smoothScroll = css`
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

export const generalBottomSpacing = css`
  margin-bottom: 20px;

  ${down('phablet')} {
    margin-bottom: 15px;
  }
`

export const sidePadding = css`
  ${down('desktop')} {
    padding-left: 20px;
    padding-right: 20px;
  }

  ${down('tablet')} {
    padding-left: 10px;
    padding-right: 10px;
  }

  ${down('phablet')} {
    padding-right: 5px;
    padding-left: 5px;
  }
`

export const mobilePadding = css`
  ${down('tablet')} {
    padding-right: 5px;
    padding-left: 5px;
  }
`

export const errorMessage = css`
  color: ${theme.colors.red};
  font-size: 0.875rem;
  padding-top: 5px;
  margin-left: 11px;
`
export const modalMessage = css`
  text-align: center;
  font-size: 1.125rem;
  width: 100%;

  .title {
    font-size: 1.5rem;
    margin-bottom: 10px;
  }
`

// TODO: Add color to theme
export const modalFooter = css`
  position: relative;
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding: 20px 0 0;
  border-top: 1px solid #ccc;
`

export const transistionValue = 150
export const transition = css`
  transition: all ${transistionValue}ms cubic-bezier(0.215, 0.61, 0.355, 1);
`

export const absoluteCenter = css`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

export const blackImageOverlay = css`
  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background: rgba(0, 0, 0, .04);
  }
`
